/**
 * Progression (design §16) — the campaign structure panel in the Levels group. One per
 * game: is it linear, gated, sets/worlds, or open-pick? Ordered entries reference levels
 * by id; validation runs the crucible-levels unlockability fixpoint live.
 */
import { useCallback, useEffect, useState } from 'react';
import type { LevelEntry } from '@volley/crucible-ipc-types';
import {
  ProgressionSchema,
  DEFAULT_PROGRESSION,
  validateProgression,
  type Progression,
} from '@volley/crucible-levels';
import { readProgression, writeProgression } from './anvil-api';
import { useCrucibleClient } from '../../transport/context.js';
import { humanizeFinding } from './finding-humanize';

export interface ProgressionState {
  progression: Progression;
  loaded: boolean;
  authored: boolean;
  save: (p: Progression) => Promise<void>;
}

export function useProgression(gameId: string): ProgressionState {
  const client = useCrucibleClient();
  const [progression, setProgression] = useState<Progression>(DEFAULT_PROGRESSION);
  const [loaded, setLoaded] = useState(false);
  const [authored, setAuthored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    readProgression(client, gameId)
      .then((raw) => {
        if (cancelled) return;
        const parsed = ProgressionSchema.safeParse(raw);
        setProgression(parsed.success ? parsed.data : DEFAULT_PROGRESSION);
        setAuthored(parsed.success);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, client]);

  const save = useCallback(
    async (next: Progression): Promise<void> => {
      await writeProgression(client, gameId, next);
      setProgression(next);
      setAuthored(true);
    },
    [gameId, client],
  );

  return { progression, loaded, authored, save };
}

/** Full display label for a level ("3 · Timber Fences") — used in the Progression editor. */
export function progressionLabel(p: Progression, levelId: string): string | null {
  const idx = p.entries.findIndex((e) => e.levelId === levelId);
  if (idx === -1) return null;
  return p.entries[idx].label ?? String(idx + 1);
}

/** Short ORDER badge for the levels nav — just the position ("1".."10"). The nav shows
 *  the level name separately, so the badge must stay a compact ordinal, never the rich
 *  label (which includes the name and overflows the fixed-width badge). Prefers a leading
 *  numeric token from a custom label, else the 1-based index. */
export function progressionOrder(p: Progression, levelId: string): string | null {
  const idx = p.entries.findIndex((e) => e.levelId === levelId);
  if (idx === -1) return null;
  const lead = p.entries[idx].label?.match(/^\s*([0-9]+)/)?.[1];
  return lead ?? String(idx + 1);
}

/** One world (a progression `set`) with its member levels, in progression order. */
export interface LevelWorldGroup {
  setId: string;
  name: string;
  levelIds: string[];
}

/** Levels grouped into worlds for the nav. `ungrouped` = roster levels with no set. */
export interface WorldGrouping {
  groups: LevelWorldGroup[];
  ungrouped: string[];
}

/**
 * Group a level roster into worlds using a `sets`-mode progression. Returns null
 * when the progression is not `sets` mode or defines no sets — the nav then falls
 * back to its flat, badge-sorted list. Groups follow `sets[]` order; levels within
 * a group follow progression entry order; roster levels with no matching set entry
 * fall into `ungrouped`. Only levels present in `levelIds` (the live roster) appear.
 */
export function progressionWorldGroups(
  p: Progression,
  levelIds: readonly string[],
): WorldGrouping | null {
  if (p.mode !== 'sets' || p.sets.length === 0) return null;
  const roster = new Set(levelIds);
  const placed = new Set<string>();
  const groups: LevelWorldGroup[] = p.sets.map((s) => {
    const ids: string[] = [];
    for (const e of p.entries) {
      if (e.set === s.id && roster.has(e.levelId) && !placed.has(e.levelId)) {
        ids.push(e.levelId);
        placed.add(e.levelId);
      }
    }
    return { setId: s.id, name: s.name ?? s.id, levelIds: ids };
  });
  const ungrouped = levelIds.filter((id) => !placed.has(id));
  return { groups, ungrouped };
}

const MODE_HELP: Record<Progression['mode'], string> = {
  linear: 'Levels play in order — finishing one unlocks the next.',
  gated: 'Each level lists the levels (and optional stars) required to unlock it.',
  sets: 'Levels group into worlds; worlds unlock as blocks.',
  open: 'Everything is available from the start — pick any level.',
};

export function ProgressionEditor({
  progression,
  authored,
  levels,
  onSave,
  onOpenLevel,
  onGenerateLevelSelect,
}: {
  progression: Progression;
  authored: boolean;
  levels: LevelEntry[];
  onSave: (p: Progression) => void;
  onOpenLevel: (levelId: string) => void;
  /** Generate a level-select Screen from this progression + roster (kept in sync). */
  onGenerateLevelSelect?: () => void;
}): React.ReactElement {
  const [draft, setDraft] = useState<Progression>(progression);
  useEffect(() => setDraft(progression), [progression]);

  const levelName = (id: string): string => levels.find((l) => l.id === id)?.name ?? id;
  const unplaced = levels.filter((l) => !draft.entries.some((e) => e.levelId === l.id));
  const findings = validateProgression(draft, new Set(levels.map((l) => l.id)));
  const check = ProgressionSchema.safeParse(draft);

  const move = (i: number, dir: -1 | 1): void => {
    const entries = [...draft.entries];
    const j = i + dir;
    if (j < 0 || j >= entries.length) return;
    [entries[i], entries[j]] = [entries[j], entries[i]];
    setDraft({ ...draft, entries });
  };
  const removeEntry = (i: number): void =>
    setDraft({ ...draft, entries: draft.entries.filter((_, x) => x !== i) });
  const addEntry = (levelId: string): void =>
    setDraft({ ...draft, entries: [...draft.entries, { levelId }] });
  const setEntry = (i: number, patch: Partial<Progression['entries'][number]>): void =>
    setDraft({
      ...draft,
      entries: draft.entries.map((e, x) => (x === i ? { ...e, ...patch } : e)),
    });

  // --- Worlds (sets mode): first-class create / rename / reorder / unlock-chain ---
  const setSets = (sets: Progression['sets']): void => setDraft({ ...draft, sets });
  const levelsInSet = (id: string): number => draft.entries.filter((e) => e.set === id).length;
  const addWorld = (): void => {
    // Unique id even after removals; new world auto-chains onto the last one.
    let k = draft.sets.length;
    let id = `world-${k}`;
    while (draft.sets.some((s) => s.id === id)) id = `world-${++k}`;
    const prev = draft.sets[draft.sets.length - 1];
    setSets([
      ...draft.sets,
      {
        id,
        name: `World ${draft.sets.length + 1}`,
        ...(prev ? { unlock: { requires: [prev.id] } } : {}),
      },
    ]);
  };
  const renameWorld = (i: number, name: string): void =>
    setSets(draft.sets.map((s, x) => (x === i ? { ...s, name } : s)));
  const moveWorld = (i: number, dir: -1 | 1): void => {
    const j = i + dir;
    if (j < 0 || j >= draft.sets.length) return;
    const sets = [...draft.sets];
    [sets[i], sets[j]] = [sets[j], sets[i]];
    setSets(sets);
  };
  const removeWorld = (i: number): void => {
    const removed = draft.sets[i];
    setDraft({
      ...draft,
      // Drop the world, scrub it from every other world's unlock requirements,
      // and unassign its levels (they fall back to "Unsorted" in the nav).
      sets: draft.sets
        .filter((_, x) => x !== i)
        .map((s) =>
          s.unlock
            ? { ...s, unlock: { requires: s.unlock.requires.filter((r) => r !== removed.id) } }
            : s,
        ),
      entries: draft.entries.map((e) => (e.set === removed.id ? { ...e, set: undefined } : e)),
    });
  };
  const toggleWorldRequire = (i: number, reqId: string): void =>
    setSets(
      draft.sets.map((s, x) => {
        if (x !== i) return s;
        const cur = s.unlock?.requires ?? [];
        const requires = cur.includes(reqId) ? cur.filter((r) => r !== reqId) : [...cur, reqId];
        return { ...s, unlock: requires.length > 0 ? { requires } : undefined };
      }),
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-medium text-crucible-fg">Progression</h2>
        <span className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-muted">
          {authored ? 'authored' : 'default (unsaved)'}
        </span>
        {onGenerateLevelSelect && (
          <button
            type="button"
            onClick={onGenerateLevelSelect}
            title="Create/update a Level Select screen from this progression — nodes = levels, notes = unlock rules"
            className="ml-auto rounded border border-crucible-accent/50 bg-crucible-accent/10 px-2 py-1 text-sm text-crucible-accent-hover hover:bg-crucible-accent/20"
          >
            ✨ Level-select screen
          </button>
        )}
        <button
          type="button"
          onClick={() => check.success && onSave(check.data)}
          disabled={!check.success}
          className={`${onGenerateLevelSelect ? '' : 'ml-auto '}rounded border border-crucible-border px-2 py-1 text-sm text-crucible-fg hover:bg-crucible-bg-elevated disabled:opacity-50`}
        >
          Save
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-crucible-fg">
          <span className="text-crucible-muted">Mode</span>
          <select
            value={draft.mode}
            onChange={(e) => setDraft({ ...draft, mode: e.target.value as Progression['mode'] })}
            className="rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
          >
            <option value="linear">Linear</option>
            <option value="gated">Gated (requirements)</option>
            <option value="sets">Sets / worlds</option>
            <option value="open">Open pick</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-crucible-fg">
          <input
            type="checkbox"
            checked={draft.replay}
            onChange={(e) => setDraft({ ...draft, replay: e.target.checked })}
          />
          <span className="text-crucible-muted">Levels can be replayed</span>
        </label>
      </div>
      <p className="text-xs text-crucible-muted">{MODE_HELP[draft.mode]}</p>

      {/* Worlds (sets mode): first-class world objects the nav groups by */}
      {draft.mode === 'sets' && (
        <div className="space-y-2 rounded-lg border border-crucible-border p-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-crucible-muted">Worlds</span>
            <button
              type="button"
              onClick={addWorld}
              className="ml-auto rounded border border-crucible-border px-2 py-0.5 text-xs text-crucible-fg hover:bg-crucible-bg-elevated"
            >
              + World
            </button>
          </div>
          {draft.sets.length === 0 && (
            <p className="text-xs text-crucible-muted">
              No worlds yet — add one, then assign levels to it below.
            </p>
          )}
          {draft.sets.map((s, i) => (
            <div
              key={s.id}
              className="flex flex-wrap items-center gap-2 rounded border border-crucible-border/60 p-1.5 text-sm"
              data-testid="progression-world-row"
            >
              <span className="w-6 text-center font-mono text-crucible-muted">{i + 1}</span>
              <input
                value={s.name ?? ''}
                onChange={(ev) => renameWorld(i, ev.target.value)}
                placeholder={s.id}
                className="w-40 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-0.5 text-sm text-crucible-fg"
              />
              <span className="text-[11px] text-crucible-muted">{levelsInSet(s.id)} levels</span>
              <span className="flex flex-wrap items-center gap-1 text-[11px] text-crucible-muted">
                unlocks after:
                {draft.sets
                  .filter((o) => o.id !== s.id)
                  .map((o) => {
                    const on = s.unlock?.requires.includes(o.id) ?? false;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => toggleWorldRequire(i, o.id)}
                        className={`rounded-full border px-1.5 py-0.5 ${
                          on
                            ? 'border-crucible-accent/50 bg-crucible-accent/15 text-crucible-accent-hover'
                            : 'border-crucible-border text-crucible-muted'
                        }`}
                      >
                        {o.name ?? o.id}
                      </button>
                    );
                  })}
                {(s.unlock?.requires.length ?? 0) === 0 && <span>(available from start)</span>}
              </span>
              <span className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveWorld(i, -1)}
                  className="px-1 text-crucible-muted hover:text-crucible-fg"
                  aria-label="Move world up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveWorld(i, 1)}
                  className="px-1 text-crucible-muted hover:text-crucible-fg"
                  aria-label="Move world down"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => removeWorld(i)}
                  className="px-1 text-red-400 hover:text-red-300"
                  aria-label="Remove world"
                >
                  ✕
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Ordered entries */}
      <div className="space-y-1.5">
        {draft.entries.map((e, i) => (
          <div
            key={e.levelId}
            className="flex flex-wrap items-center gap-2 rounded border border-crucible-border p-2 text-sm"
          >
            <span className="w-7 text-center font-mono text-crucible-muted">
              {e.label ?? i + 1}
            </span>
            <button
              type="button"
              onClick={() => onOpenLevel(e.levelId)}
              className="min-w-0 flex-1 truncate text-left text-crucible-fg hover:text-crucible-accent-hover"
              title="Open this level"
            >
              {levelName(e.levelId)}
            </button>
            {draft.mode === 'sets' && (
              <select
                value={e.set ?? ''}
                onChange={(ev) => setEntry(i, { set: ev.target.value || undefined })}
                title="Which world this level belongs to"
                className="w-32 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-0.5 text-xs text-crucible-fg"
              >
                <option value="">— no world —</option>
                {draft.sets.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name ?? s.id}
                  </option>
                ))}
              </select>
            )}
            {draft.mode === 'gated' && (
              <span className="flex flex-wrap items-center gap-1 text-[11px] text-crucible-muted">
                needs:
                {draft.entries.slice(0, i).map((prev) => {
                  const on = e.unlock?.requires.includes(prev.levelId) ?? false;
                  return (
                    <button
                      key={prev.levelId}
                      type="button"
                      onClick={() => {
                        const cur = e.unlock?.requires ?? [];
                        const requires = on
                          ? cur.filter((r) => r !== prev.levelId)
                          : [...cur, prev.levelId];
                        setEntry(i, {
                          unlock: requires.length > 0 ? { requires } : undefined,
                        });
                      }}
                      className={`rounded-full border px-1.5 py-0.5 ${
                        on
                          ? 'border-crucible-accent/50 bg-crucible-accent/15 text-crucible-accent-hover'
                          : 'border-crucible-border text-crucible-muted'
                      }`}
                    >
                      {levelName(prev.levelId)}
                    </button>
                  );
                })}
                {i === 0 && <span>(first level)</span>}
              </span>
            )}
            <span className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => move(i, -1)}
                className="px-1 text-crucible-muted hover:text-crucible-fg"
                aria-label="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                className="px-1 text-crucible-muted hover:text-crucible-fg"
                aria-label="Move down"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => removeEntry(i)}
                className="px-1 text-red-400 hover:text-red-300"
                aria-label="Remove"
              >
                ✕
              </button>
            </span>
          </div>
        ))}
        {draft.entries.length === 0 && (
          <p className="text-sm text-crucible-muted">No levels placed yet — add them below.</p>
        )}
      </div>

      {unplaced.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wide text-crucible-muted">Add</span>
          {unplaced.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => addEntry(l.id)}
              className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-fg hover:bg-crucible-bg-elevated"
            >
              + {l.name}
            </button>
          ))}
        </div>
      )}

      {/* Live validation */}
      <div className="rounded-lg border border-crucible-border p-2">
        <div className="mb-1 text-[11px] uppercase tracking-wide text-crucible-muted">
          Validation
          {findings.every((f) => f.severity !== 'error') && (
            <span className="ml-2 text-emerald-300">✓ every level unlockable</span>
          )}
        </div>
        {findings.length === 0 ? (
          <p className="text-xs text-emerald-300">No issues found.</p>
        ) : (
          <ul className="space-y-1">
            {findings.map((f, i) => {
              const h = humanizeFinding(f);
              return (
                <li key={i} className="text-xs">
                  <span className={f.severity === 'error' ? 'text-red-400' : 'text-amber-300'}>
                    {h.title}
                  </span>
                  {h.fix && <span className="text-crucible-muted"> → {h.fix}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
